import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBook, faClipboard, faPlus, faTrash, faCheck } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatRFCDate, dateToRFCDateValue } from '@/utils/helpers';
import { showSuccessToast, showErrorToast } from '@/utils/toasts';
import type { JournalReview, JournalTemplate } from '@/models';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Tabs from '@/components/ui/Tabs';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import Modal from '@/components/ui/Modal';
import {
  JOURNAL_FIELD_CLASS,
  JOURNAL_LABEL_CLASS,
  reviewStatusBadgeClass,
  reviewStatusLabel,
} from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';
import type { JournalOutletContext } from '@/components/journal/JournalLayout';

type Tab = 'reviews' | 'templates';

interface ReviewsData {
  reviews: JournalReview[];
  templates: JournalTemplate[];
}

/** The textarea value plus the server value it was seeded from, so edits survive a reload. */
interface NoteState {
  draft: string;
  server: string;
}

type BusyKind = 'createReview' | 'saveNotes' | 'toggleDone' | 'deleteReview' | 'createTemplate' | 'deleteTemplate';

interface BusyAction {
  kind: BusyKind;
  /** The affected review/template, or null for the create forms. */
  id: string | null;
}

export default function JournalReviewsPage() {
  const today = dateToRFCDateValue(new Date());
  const { refreshDueReviews } = useOutletContext<JournalOutletContext>();
  const [tab, setTab] = useState<Tab>('reviews');

  const { data, loading, error, reload } = useJournalResource<ReviewsData>(
    async () => {
      const [reviewsResp, templatesResp] = await Promise.all([
        APIRoutes.listJournalReviews({ limit: 200 }),
        APIRoutes.listJournalTemplates(),
      ]);
      return {
        reviews: [...reviewsResp.reviews].sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor)),
        templates: templatesResp.templates,
      };
    },
    [],
    'Fehler beim Laden der Reviews',
  );
  const reviews: JournalReview[] = data?.reviews ?? [];
  const templates: JournalTemplate[] = data?.templates ?? [];

  const [busyAction, setBusyAction] = useState<BusyAction | null>(null);
  const busy = busyAction !== null;
  const isBusyWith = (kind: BusyKind, id: string | null = null): boolean =>
    busyAction !== null && busyAction.kind === kind && busyAction.id === id;

  // create-review form
  const [rvTitle, setRvTitle] = useState('');
  const [rvDate, setRvDate] = useState(today);
  const [rvTemplateID, setRvTemplateID] = useState('');

  // create-template form
  const [tplName, setTplName] = useState('');
  const [tplBody, setTplBody] = useState('');

  const [notes, setNotes] = useState<Record<string, NoteState>>({});
  const [deleteReviewTarget, setDeleteReviewTarget] = useState<JournalReview | null>(null);
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState<JournalTemplate | null>(null);

  // Merges instead of replaces: a draft the user has edited is never overwritten by a reload.
  useEffect(() => {
    if (data === null) return;
    setNotes((prev) => {
      const next: Record<string, NoteState> = {};
      for (const review of data.reviews) {
        const existing = prev[review.id];
        const edited = existing !== undefined && existing.draft !== existing.server;
        next[review.id] = { draft: edited ? existing.draft : review.notes, server: review.notes };
      }
      return next;
    });
  }, [data]);

  const noteDraft = (review: JournalReview): string => notes[review.id]?.draft ?? review.notes;
  const noteIsDirty = (review: JournalReview): boolean => {
    const state = notes[review.id];
    return state !== undefined && state.draft !== state.server;
  };

  const setNoteDraft = (review: JournalReview, draft: string) => {
    setNotes((prev) => ({ ...prev, [review.id]: { draft, server: prev[review.id]?.server ?? review.notes } }));
  };

  const persistNotes = async (review: JournalReview, draft: string) => {
    await APIRoutes.updateJournalReview(review.id, { notes: draft });
    setNotes((prev) => ({ ...prev, [review.id]: { draft, server: draft } }));
  };

  const templateName = (id: string | null): string | null => {
    if (id === null) return null;
    const tpl = templates.find((t) => t.id === id);
    return tpl !== undefined ? tpl.name : null;
  };

  const handleCreateReview = async () => {
    if (rvTitle.trim() === '') return;
    setBusyAction({ kind: 'createReview', id: null });
    try {
      const selectedTemplate = templates.find((t) => t.id === rvTemplateID);
      await APIRoutes.createJournalReview({
        title: rvTitle.trim(),
        scheduledFor: rvDate,
        templateID: rvTemplateID === '' ? null : (rvTemplateID as JournalTemplate['id']),
        notes: selectedTemplate !== undefined ? selectedTemplate.body : null,
      });
      setRvTitle('');
      setRvTemplateID('');
      setRvDate(today);
      showSuccessToast('Review geplant');
      reload();
      refreshDueReviews();
    } catch (err) {
      console.error('Failed to create review:', err);
      showErrorToast('Review konnte nicht geplant werden');
    } finally {
      setBusyAction(null);
    }
  };

  const handleSaveNotes = async (review: JournalReview) => {
    setBusyAction({ kind: 'saveNotes', id: review.id });
    try {
      await persistNotes(review, noteDraft(review));
      showSuccessToast('Notiz gespeichert');
    } catch (err) {
      console.error('Failed to save notes:', err);
      showErrorToast('Notiz konnte nicht gespeichert werden');
    } finally {
      setBusyAction(null);
    }
  };

  const handleToggleDone = async (review: JournalReview) => {
    setBusyAction({ kind: 'toggleDone', id: review.id });
    try {
      // The status change reloads the list - persist a pending note first or it would be lost.
      if (noteIsDirty(review)) await persistNotes(review, noteDraft(review));
      const next = review.status === 'DONE' ? 'OPEN' : 'DONE';
      await APIRoutes.updateJournalReview(review.id, { status: next });
      showSuccessToast(next === 'DONE' ? 'Review erledigt' : 'Review wieder geöffnet');
      reload();
      refreshDueReviews();
    } catch (err) {
      console.error('Failed to update review status:', err);
      showErrorToast('Status konnte nicht geändert werden');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeleteReview = async (review: JournalReview) => {
    setBusyAction({ kind: 'deleteReview', id: review.id });
    try {
      await APIRoutes.deleteJournalReview(review.id);
      setDeleteReviewTarget(null);
      showSuccessToast('Review gelöscht');
      reload();
      refreshDueReviews();
    } catch (err) {
      console.error('Failed to delete review:', err);
      showErrorToast('Review konnte nicht gelöscht werden');
    } finally {
      setBusyAction(null);
    }
  };

  const handleCreateTemplate = async () => {
    if (tplName.trim() === '') return;
    setBusyAction({ kind: 'createTemplate', id: null });
    try {
      await APIRoutes.createJournalTemplate({ name: tplName.trim(), body: tplBody });
      setTplName('');
      setTplBody('');
      showSuccessToast('Vorlage erstellt');
      reload();
    } catch (err) {
      console.error('Failed to create template:', err);
      showErrorToast('Vorlage konnte nicht erstellt werden');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeleteTemplate = async (tpl: JournalTemplate) => {
    setBusyAction({ kind: 'deleteTemplate', id: tpl.id });
    try {
      await APIRoutes.deleteJournalTemplate(tpl.id);
      setDeleteTemplateTarget(null);
      showSuccessToast('Vorlage gelöscht');
      reload();
    } catch (err) {
      console.error('Failed to delete template:', err);
      showErrorToast('Vorlage konnte nicht gelöscht werden');
    } finally {
      setBusyAction(null);
    }
  };

  const dueCount = reviews.filter((r) => r.status === 'OPEN' && r.scheduledFor <= today).length;

  return (
    <div className="space-y-4">
      <Tabs
        items={[
          { id: 'reviews', label: 'Reviews', badge: dueCount },
          { id: 'templates', label: 'Vorlagen' },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id === 'templates' ? 'templates' : 'reviews')}
      />

      {tab === 'reviews' ? (
        <Card>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[12rem]">
              <label className={JOURNAL_LABEL_CLASS} htmlFor="review-title">Titel</label>
              <input id="review-title" className={`${JOURNAL_FIELD_CLASS} w-full`} value={rvTitle} onChange={(e) => setRvTitle(e.target.value)} placeholder="z.B. Wochenrückblick KW 26" />
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="review-date">Fällig am</label>
              <input id="review-date" className={JOURNAL_FIELD_CLASS} type="date" value={rvDate} onChange={(e) => setRvDate(e.target.value)} />
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="review-template">Vorlage</label>
              <select id="review-template" className={JOURNAL_FIELD_CLASS} value={rvTemplateID} onChange={(e) => setRvTemplateID(e.target.value)}>
                <option value="">Keine</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <Button icon={faPlus} loading={isBusyWith('createReview')} disabled={busy || rvTitle.trim() === '' || rvDate === ''} onClick={handleCreateReview}>
              Planen
            </Button>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="space-y-3">
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="template-name">Name</label>
              <input id="template-name" className={`${JOURNAL_FIELD_CLASS} w-full`} value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="z.B. Trade-Review-Checkliste" />
            </div>
            <div>
              <label className={JOURNAL_LABEL_CLASS} htmlFor="template-body">Inhalt</label>
              <textarea id="template-body" className={`${JOURNAL_FIELD_CLASS} w-full`} rows={4} value={tplBody} onChange={(e) => setTplBody(e.target.value)} placeholder={'1. Was war der Plan?\n2. Habe ich mich daran gehalten?\n3. Was lerne ich?'} />
            </div>
            <div className="flex justify-end">
              <Button icon={faPlus} loading={isBusyWith('createTemplate')} disabled={busy || tplName.trim() === ''} onClick={handleCreateTemplate}>
                Vorlage anlegen
              </Button>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-16" role="status" aria-label="Lädt">
          <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : error !== null ? (
        <ErrorState message={error} onRetry={reload} />
      ) : tab === 'reviews' ? (
        reviews.length === 0 ? (
          <EmptyState
            icon={faBook}
            title="Noch keine Reviews geplant"
            description="Plane oben dein erstes Review, um deine Trades regelmäßig nachzubereiten."
          />
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <Card key={review.id}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{review.title}</span>
                    <Badge colorClass={reviewStatusBadgeClass(review, today)}>{reviewStatusLabel(review, today)}</Badge>
                    {templateName(review.templateID) !== null && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">· {templateName(review.templateID)}</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{formatRFCDate(review.scheduledFor)}</span>
                </div>
                <textarea
                  className={`${JOURNAL_FIELD_CLASS} w-full`}
                  rows={3}
                  value={noteDraft(review)}
                  onChange={(e) => setNoteDraft(review, e.target.value)}
                  placeholder="Notizen zum Review..."
                  aria-label={`Notizen zu "${review.title}"`}
                />
                <div className="flex flex-wrap justify-end gap-2 mt-2">
                  <Button
                    variant={noteIsDirty(review) ? 'primary' : 'secondary'}
                    size="sm"
                    loading={isBusyWith('saveNotes', review.id)}
                    disabled={busy || !noteIsDirty(review)}
                    onClick={() => handleSaveNotes(review)}
                  >
                    Notiz speichern
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={faCheck}
                    loading={isBusyWith('toggleDone', review.id)}
                    disabled={busy}
                    onClick={() => handleToggleDone(review)}
                  >
                    {review.status === 'DONE' ? 'Wieder öffnen' : 'Erledigt'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={faTrash}
                    title={`Review "${review.title}" löschen`}
                    aria-label={`Review "${review.title}" löschen`}
                    disabled={busy}
                    onClick={() => setDeleteReviewTarget(review)}
                  />
                </div>
              </Card>
            ))}
          </div>
        )
      ) : templates.length === 0 ? (
        <EmptyState
          icon={faClipboard}
          title="Noch keine Vorlagen"
          description="Lege oben eine Vorlage an, damit neue Reviews direkt mit deiner Checkliste starten."
        />
      ) : (
        <div className="space-y-3">
          {templates.map((tpl) => (
            <Card key={tpl.id}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-medium text-gray-900 dark:text-white truncate">{tpl.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={faTrash}
                  title={`Vorlage "${tpl.name}" löschen`}
                  aria-label={`Vorlage "${tpl.name}" löschen`}
                  disabled={busy}
                  onClick={() => setDeleteTemplateTarget(tpl)}
                />
              </div>
              <pre className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words font-sans">{tpl.body}</pre>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={deleteReviewTarget !== null}
        onClose={() => setDeleteReviewTarget(null)}
        title="Review löschen"
        maxWidth="md"
        closeDisabled={busy}
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {deleteReviewTarget !== null && `Review "${deleteReviewTarget.title}" wirklich löschen? Die Notizen gehen dabei verloren, das lässt sich nicht rückgängig machen.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => setDeleteReviewTarget(null)}>Abbrechen</Button>
            <Button
              variant="danger"
              loading={deleteReviewTarget !== null && isBusyWith('deleteReview', deleteReviewTarget.id)}
              onClick={() => { if (deleteReviewTarget !== null) void handleDeleteReview(deleteReviewTarget); }}
            >
              Löschen
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteTemplateTarget !== null}
        onClose={() => setDeleteTemplateTarget(null)}
        title="Vorlage löschen"
        maxWidth="md"
        closeDisabled={busy}
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {deleteTemplateTarget !== null && `Vorlage "${deleteTemplateTarget.name}" wirklich löschen? Das lässt sich nicht rückgängig machen.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => setDeleteTemplateTarget(null)}>Abbrechen</Button>
            <Button
              variant="danger"
              loading={deleteTemplateTarget !== null && isBusyWith('deleteTemplate', deleteTemplateTarget.id)}
              onClick={() => { if (deleteTemplateTarget !== null) void handleDeleteTemplate(deleteTemplateTarget); }}
            >
              Löschen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
